/**
 * Deal stage constants and mappings
 * Migrated from hubspot-deals frontend
 */

// All deal stages with HubSpot IDs
export const DEAL_STAGES = [
  { id: '', label: 'All Stages' },
  { id: 'appointmentscheduled', label: 'Quote Requested' },
  { id: '4383256790', label: 'Quote Ready' },
  { id: 'qualifiedtobuy', label: 'Quote Sent' },
  { id: '2136864980', label: 'Warehouse Drop Off' },
  { id: '597442256', label: 'Awaiting Shipping Label' },
  { id: '4367683815', label: 'Awaiting Collection' },
  { id: 'presentationscheduled', label: 'Shipping Arranged' },
  { id: '835164638', label: 'Arrived To Test' },
  { id: 'contractsent', label: 'Awaiting Payment' },
  { id: 'closedwon', label: 'Closed Traded' },
  { id: 'closedlost', label: 'Closed Lost' },
  { id: '589630139', label: 'Quote Chased' },
  { id: '589142218', label: 'Problem With Shipping' },
  { id: '588059587', label: 'Issue with Item' },
  { id: '609520315', label: 'Consignment Offered' },
  { id: '853396936', label: 'Consignment Listing' },
  { id: '638945017', label: 'Last Chance Offer' },
  { id: '901659893', label: 'More Info Needed' },
  { id: '1616888039', label: 'Duplicate Trade Request' },
  { id: '4962263243', label: 'Part Ex' },
  { id: '4311086309', label: 'Artifacts' }
]

/**
 * Prism's canonical stages that no HubSpot stage ever meant.
 *
 * `expired` and `collected` are forward-flow-only, so they have no HubSpot id
 * and cannot live in DEAL_STAGES — which is the WRITABLE vocabulary, driving the
 * stage-change selects in DealDetailModal and QuoteRequested and the create
 * dropdown in CreateDeal. Expiry is a scheduler transition and there is no
 * HubSpot id to write, so offering it to an operator would be offering a button
 * that cannot work.
 *
 * These are filter-and-display only. `id` is the canonical stage itself, which
 * is what the API takes: A1's `stage` param is the QuoteStage enum.
 */
export const CANONICAL_ONLY_STAGES = [
  { id: 'expired', label: 'Expired' },
  { id: 'collected', label: 'Collected' },
  { id: 'awaiting_customer', label: 'Awaiting Customer Response' }
]

/**
 * Everything an operator can FILTER by — both vocabularies.
 *
 * Kept separate from DEAL_STAGES so the two never leak into each other: this
 * list may only ever be read by a search control. 13,048 quotes sit at
 * `expired`, and without an option here there is no way to reach any of them.
 */
export const FILTERABLE_STAGES = [...DEAL_STAGES, ...CANONICAL_ONLY_STAGES]

/**
 * The status label for a deal row.
 *
 * `stageName` comes from the API, which resolves both vocabularies and knows
 * things this file cannot — an `accepted` quote reads "Awaiting Collection" or
 * "Warehouse Drop Off" depending on its booking. Deriving the label here from
 * `dealstage` instead is what left 13,048 expired rows with an empty badge:
 * their `dealstage` is legitimately null, and `getStageName(null)` is null.
 */
export function dealStageLabel(deal) {
  return deal?.stageName || getStageName(deal?.dealstage) || getStageName(deal?.stage) || ''
}

// Stages where parsed items can be edited
export const EDITABLE_STAGES = [
  'appointmentscheduled',  // Quote Requested
  '4383256790',            // Quote Ready
  'qualifiedtobuy'         // Quote Sent
]

// Stages where item SP/BP may be edited in the deal modal: any OPEN deal.
// Closed deals are the lock — a paid payout must not drift under a
// retroactive price edit. (Operator ruling 2026-07-28: all open stages,
// e.g. revising an offer on an Issue with Item deal after testing.)
export const PRICE_LOCKED_STAGES = ['closedwon', 'closedlost']

// Item states at or beyond testing (Stream D, spec_20260808): a testing record
// exists, so the buy price shown is the committed payout and price editing is
// locked. This is the ROBUST signal — it rides on the A4 item, so a transient
// /test-results fetch failure cannot unlock a tested item back to an editable
// Valuation. Pre-test states (pending, received) are absent by design.
export const TESTED_ITEM_STATES = ['tested_ok', 'issue', 'in_repair', 'return_requested', 'listed', 'sold']

// Get stage label from ID — a HubSpot stage id or a canonical Prism stage
export function getStageName(stageId) {
  const stage = DEAL_STAGES.find(s => s.id === stageId)
    || CANONICAL_ONLY_STAGES.find(s => s.id === stageId)
  return stage ? stage.label : stageId
}

// Extract search term from parsed item line
// Handles both old format: "1. Citronic CMC16 - Mixing Console - Qty:1 - SP:£0 - BP:£0"
// and new format: "Fender Strat, excellent, qty 6"
// Returns just the product name (e.g. "Citronic CMC16" or "Fender Strat")
export function getSearchTerm(text) {
  if (!text) return ''

  // Remove leading number and period (e.g., "1. " or "12. ")
  let cleaned = text.replace(/^\d+\.\s*/, '')

  // Find the first " - " (space-dash-space) and take everything before it
  const dashIndex = cleaned.indexOf(' - ')
  if (dashIndex > 0) {
    cleaned = cleaned.substring(0, dashIndex)
  }

  // Strip trailing condition/qty from comma-separated format
  const knownConditions = ['mint', 'like new', 'open box', 'excellent', 'very good', 'good', 'fair', 'poor', 'broken']
  let changed = true
  while (changed) {
    changed = false
    const commaIndex = cleaned.lastIndexOf(',')
    if (commaIndex > 0) {
      const after = cleaned.substring(commaIndex + 1).trim().toLowerCase()
      if (knownConditions.includes(after) || /^qty\s+\d+$/i.test(after)) {
        cleaned = cleaned.substring(0, commaIndex).trim()
        changed = true
      }
    }
  }

  return cleaned.trim()
}

// Generate search URLs for external marketplaces.
// When `executedTerm` is provided (the exact string the pricing worker sent to
// the marketplaces, from deal_diagnostics.executed_search_term), use it verbatim
// so the buttons match the "Search term used" line in the Market comps panel.
// Otherwise fall back to the frontend-derived term off the current item name.
export function getSearchUrls(title, executedTerm) {
  const term = executedTerm ? executedTerm : getSearchTerm(title)
  const searchTerm = encodeURIComponent(term)
  return {
    reverbSold: `https://reverb.com/uk/marketplace?query=${searchTerm}&show_only_sold=true`,
    reverbActive: `https://reverb.com/uk/marketplace?query=${searchTerm}`,
    ebayActive: `https://www.ebay.co.uk/sch/i.html?_nkw=${searchTerm}&_sacat=0&LH_BIN=1&_dmd=2&rt=nc`,
    ebaySold: `https://www.ebay.co.uk/sch/i.html?_nkw=${searchTerm}&_sacat=0&LH_Complete=1&LH_Sold=1&rt=nc&LH_BIN=1&_dmd=2&rt=nc`,
    // UK Google Shopping: tbm=shop → Shopping tab, gl=GB → UK results, hl=en → English.
    // Pins results to the UK regardless of where the operator is browsing from.
    google: `https://www.google.com/search?tbm=shop&q=${searchTerm}&gl=GB&hl=en`
  }
}

// Parse items from parsed_items text field
export function parseItems(parsedItemsText) {
  if (!parsedItemsText) return []
  return parsedItemsText.split('\n').filter(line => line.trim())
}

// Parse a single item line into structured data
// Handles both old format: "1. Korg Krome 88 - Digital Piano - Qty:1 - SP:£709 - BP:£351"
// and new format: "Fender Strat, excellent, qty 6"
export function parseItemLine(line) {
  if (!line) return null

  const text = line.replace(/^\d+\.\s*/, '')

  // A real price tag contains ':' or '£' ("SP:£15"); a bare "SP-10"/"BP-8" is a model
  // name (Fender SP-10, Technics SP-10). Take the LAST tag — tags follow the name.
  const lastPriceTag = (label) => {
    const ms = [...text.matchAll(new RegExp(`\\b${label}[-:.]?\\s*£?([\\d,.]+)`, 'gi'))]
      .filter(m => /[:£]/.test(m[0]))
    return ms.length ? ms[ms.length - 1] : null
  }
  const spMatch = lastPriceTag('SP')
  const bpMatch = lastPriceTag('BP')
  const qtyMatch = text.match(/\bQty[:\s]\s*(\d+)/i)
  const srcMatch = text.match(/\[src:(\d+)\]/i)
  const pxMatch = text.match(/\bPX:(Yes|No)/i)
  const scoreMatch = text.match(/\bSc:(\d+)/i)
  const aiMatch = /\s-\sAI\b/i.test(text)
  const lowConfidence = /\[LOW CONFIDENCE\]/i.test(text)

  const sp = spMatch ? parseFloat(spMatch[1].replace(',', '')) : null
  const bp = bpMatch ? parseFloat(bpMatch[1].replace(',', '')) : null
  const qty = qtyMatch ? parseInt(qtyMatch[1]) : 1
  const sourceId = srcMatch ? srcMatch[1] : null
  const isPx = pxMatch ? pxMatch[1].toLowerCase() === 'yes' : false
  const score = scoreMatch ? parseInt(scoreMatch[1], 10) : null

  // Strip only real price tags (':' or '£' present) — keep "SP-10" model names intact.
  let description = text
    .replace(/\s*-?\s*\bSP[-:.]?\s*£?[\d,.]+/gi, m => /[:£]/.test(m) ? '' : m)
    .replace(/\s*-?\s*\bBP[-:.]?\s*£?[\d,.]+/gi, m => /[:£]/.test(m) ? '' : m)
    .replace(/\s*-\s*Qty[:\s]\s*\d+/gi, '')
    .replace(/,\s*qty\s+\d+/gi, '')
    .replace(/\s*\[src:\d+\]/gi, '')
    .replace(/\s*-?\s*\bPX:(Yes|No)/gi, '')
    .replace(/\s*-\s*Sc:\d+/gi, '')
    .replace(/\s*-\s*AI\b/gi, '')
    .replace(/\s*\[LOW CONFIDENCE\]/gi, '')
    .trim()

  const knownConditions = ['mint', 'like new', 'open box', 'excellent', 'very good', 'good', 'fair', 'poor', 'broken']
  let condition = null

  const dashParts = description.split(' - ').map(p => p.trim()).filter(Boolean)
  if (dashParts.length >= 2) {
    const lastDash = dashParts[dashParts.length - 1].toLowerCase()
    if (knownConditions.includes(lastDash)) {
      condition = dashParts.pop().trim()
      description = dashParts.join(' - ')
    }
  }

  if (!condition) {
    const descParts = description.split(',').map(p => p.trim())
    if (descParts.length >= 2) {
      const lastPart = descParts[descParts.length - 1].toLowerCase()
      if (knownConditions.includes(lastPart)) {
        condition = descParts.pop().trim()
        description = descParts.join(', ').trim()
      }
    }
  }

  // Normalize customer-facing condition labels to canonical DB values.
  // "Like New"/"Open Box" are how we show Mint to customers; store Mint.
  if (condition) {
    const cl = condition.toLowerCase()
    if (cl === 'like new' || cl === 'open box') condition = 'Mint'
  }

  const parts = description.split(' - ')
  const name = parts[0] || description
  const details = parts.slice(1).join(' · ')

  return { name, details, condition, qty, sp, bp, score, aiPriceSource: aiMatch, sourceId, isPx, lowConfidence, raw: line }
}

// The deal's value is the sum of its items' buy prices — the same rule the
// payout reconciler enforces (a payment is blocked when they disagree) and the
// rule Prism derives `amount` by. BP is stored PER UNIT, so each line
// contributes BP × Qty, matching Prism's amount_cents and the reconciler
// (workers/src/utils/parsedItems.js). A deal with no BP tags predates per-item
// pricing, so the stored amount is the only figure there is.
export function totalBuyPrice(parsedItemsText, amount) {
  let sum = 0
  let hasBp = false
  for (const line of parseItems(parsedItemsText)) {
    const item = parseItemLine(line)
    if (item && Number.isFinite(item.bp)) {
      sum += item.bp * (item.qty || 1)
      hasBp = true
    }
  }
  return hasBp ? sum : (parseFloat(amount) || 0)
}

/**
 * The deal identifier to SHOW — not the one to address the API with.
 *
 * `deal.id` is `hubspot_dealid ?? quote_id`, so for a Prism-native quote it is
 * the quote UUID. Every surface the operator compares against (deal search,
 * testing, emails) shows the HubSpot deal id or, failing that, the reference
 * code — never the UUID. `id === quote_id` is exactly "no HubSpot id".
 * API paths keep using `deal.id`; only display sites go through here.
 *
 * @param {object|null|undefined} deal — deal detail / grid row shape
 * @returns {string} identifier for display; '' when there is no deal
 */
export function displayDealId(deal) {
  if (!deal) return ''
  const hasHubspotId = deal.id != null && deal.id !== deal.quote_id
  if (hasHubspotId) return String(deal.id)
  return deal.reference_code || deal.id || ''
}

/**
 * What the operator typed into a Sell box: a single price ("100") or a
 * Sell/Buy pair ("100/41", "£100 / £41").
 *
 * The pair exists because an item's FIRST price can only be created with both
 * halves at once: Prism stores a price as Sell+Buy against the grade, and the
 * engine cannot derive a BP for an item it has never priced — e.g. one just
 * added to a quote. A lone SP and a lone BP each refuse in that state, so
 * without the pair the item is manually unpriceable. The notation mirrors the
 * blob's own "SP:£100 / BP:£41".
 *
 * @param {string} raw the Sell box's text
 * @returns {{sp: number, bp: number|null}|null} null when unparseable
 */
export function parseSellEntry(raw) {
  const text = String(raw ?? '').trim()
  if (!text) return null
  const pair = text.match(/^£?\s*(\d+(?:\.\d+)?)\s*\/\s*£?\s*(\d+(?:\.\d+)?)$/)
  if (pair) return { sp: parseFloat(pair[1]), bp: parseFloat(pair[2]) }
  // Number, not parseFloat: parseFloat('100/') reads 100 and would silently
  // drop a half-typed pair's BP.
  const single = Number(text.replace(/^£\s*/, ''))
  return Number.isFinite(single) && single >= 0 ? { sp: single, bp: null } : null
}
